import {
    Block,
    BlockRange,
    type Inline,
    type SelectionRange,
    Text,
    type Value,
} from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';

import {
    BLOCK_LIST_ITEM,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
} from '../types';

/**
 * Test if a block is a list.
 */
export function isList(block: Block): block is Block & {
    type: typeof BLOCK_LIST_OL | typeof BLOCK_LIST_UL | typeof BLOCK_LIST_TASKS;
} {
    return [BLOCK_LIST_OL, BLOCK_LIST_UL, BLOCK_LIST_TASKS].includes(block.type);
}

/**
 * Create an empty item
 */
export function createListItem(task = false, nodes?: Array<Inline | Text>): Block {
    return Block.create({
        type: BLOCK_LIST_ITEM,
        data: task ? { checked: false } : {},
        nodes: [
            Block.create({
                type: BLOCK_PARAGRAPH,
                nodes: nodes || [Text.create('')],
            }),
        ],
    });
}

/**
 * Create an empty list
 */
export function createList(
    type: typeof BLOCK_LIST_OL | typeof BLOCK_LIST_UL | typeof BLOCK_LIST_TASKS,
    nodes?: Array<Inline | Text>
): Block {
    return Block.create({
        type,
        nodes: [createListItem(type === BLOCK_LIST_TASKS, nodes)],
    });
}

/**
 * Return the current list item, from current selection or from a node.
 */
export function getCurrentItem(value: Value, block?: Block): Block | null {
    const { document } = value;

    const resolvedBlock = block ?? value.startBlock;
    if (!resolvedBlock) {
        return null;
    }

    if (resolvedBlock.type === BLOCK_LIST_ITEM) {
        return resolvedBlock;
    }

    const parent = document.getParent(resolvedBlock.key);
    return Block.isBlock(parent) && parent.type === BLOCK_LIST_ITEM ? parent : null;
}

/**
 * Return the parent list block, from current selection or from a node (paragraph in a list item).
 */
export function getCurrentList(value: Value, block?: Block): Block | null {
    const item = getCurrentItem(value, block);

    if (!item) {
        return null;
    }

    return getListForItem(value, item);
}

/**
 * Return the parent list block for an item block.
 */
export function getListForItem(value: Value, item: Block): Block | null {
    const { document } = value;
    const parent = document.getParent(item.key) as Block | null;
    return parent && isList(parent) ? parent : null;
}

/**
 * Get depth of current block in a document list
 */
export function getItemDepth(value: Value, block?: Block): number {
    const { document, startBlock } = value;
    const currentItem = getCurrentItem(value, block ?? startBlock ?? undefined);

    if (!currentItem) {
        return 0;
    }

    const parent = document.getParent(currentItem.key);
    const parentDepth = Block.isBlock(parent) ? getItemDepth(value, parent) : 0;
    return 1 + parentDepth;
}

/**
 * Return the list of items at the given range. The returned items are
 * the highest list item blocks that cover the range.
 *
 * Returns an empty list if no list of items can cover the range
 */
export function getItemsAtRange(
    value: Value,
    range: SelectionRange = value.selection
): List<Block> {
    if (BlockRange.isBlockRange(range)) {
        return range.getBlocks(value.document);
    }

    if (!range.startKey || !range.endKey) {
        return List.create();
    }

    const { document } = value;

    const startBlock = document.getClosestBlock(range.startKey);
    const endBlock = document.getClosestBlock(range.endKey);

    if (!startBlock || !endBlock) {
        return List.create();
    }

    if (startBlock === endBlock) {
        const item = getCurrentItem(value, startBlock);
        return item ? List.create([item]) : List.create();
    }

    const ancestor = document.getCommonAncestor(startBlock.key, endBlock.key);

    if (!Block.isBlock(ancestor)) {
        return List.create();
    }

    if (isList(ancestor)) {
        const startPath = ancestor.getPath(startBlock.key);
        const endPath = ancestor.getPath(endBlock.key);
        const start = startPath?.[0];
        const end = endPath?.[0];

        if (start === undefined || end === undefined) {
            return List.create();
        }

        return ancestor.nodes
            .slice(start, end + 1)
            .filter((node) => Block.isBlock(node)) as List<Block>;
    }

    if (ancestor.type === BLOCK_LIST_ITEM) {
        // The ancestor is the highest list item that covers the range
        return List.create([ancestor]);
    }

    // No list of items can cover the range
    return List.create();
}

/**
 * Return the previous item, from current selection or from a node.
 */
export function getPreviousItem(value: Value, block?: Block): Block | null {
    const { document, startBlock } = value;
    const resolvedBlock = block ?? startBlock ?? undefined;

    const currentItem = getCurrentItem(value, resolvedBlock);

    if (!currentItem) {
        return null;
    }

    const previousSibling = document.getPreviousSibling(currentItem.key);
    if (Block.isBlock(previousSibling) && previousSibling.type === BLOCK_LIST_ITEM) {
        return previousSibling;
    }

    return null;
}

/**
 * True if selection is inside a list (and can be unwrapped)
 */
export function isSelectionInList(
    value: Value,
    type?: typeof BLOCK_LIST_OL | typeof BLOCK_LIST_UL | typeof BLOCK_LIST_TASKS
): boolean {
    const items = getItemsAtRange(value);
    const firstItem = items.first();
    if (!firstItem) {
        return false;
    }
    const list = getListForItem(value, firstItem);
    if (!list) {
        return false;
    }
    return !type || list.type === type;
}
